import React from 'react';

interface RazaLoaderProps {
    /** Optional message below the spinner */
    message?: string;
    /** Size variant: 'sm' for inline, 'md' for sections, 'lg' for full-page */
    size?: 'sm' | 'md' | 'lg';
    /** If true, renders as a full-screen overlay */
    fullScreen?: boolean;
}

/** Diámetro del aro en px. `lg` es el mismo 64 que dibuja `index.html`. */
const SIZE_PX = { sm: 24, md: 40, lg: 64 } as const;

/**
 * A tamaño natural la hoja se muestra en celdas de 72 px, y el aro dibujado
 * dentro de cada celda mide 64. La caja siempre es un poco mayor que el aro
 * porque el fotograma trae margen — sin él, las puntas redondas se cortarían
 * contra el borde del recorte.
 */
const CAJA_NATURAL = 72;
const ARO_NATURAL = 64;

/**
 * RazaLoader — la pantalla de carga de la app.
 *
 * Dibuja exactamente lo mismo que `index.html` pinta antes de que React exista,
 * con las mismas clases (ver `.rz-aro` en index.css). No es una imitación: es
 * el mismo marcado, así que las dos fases del arranque son indistinguibles.
 *
 * La animación son los 180 fotogramas del Lottie original rasterizados en una
 * hoja, movidos con `transform`. Se hizo así porque un Lottie se redibuja con
 * JavaScript en el hilo principal, y ese hilo pasa 134 ms seguidos bloqueado
 * parseando el bundle justo mientras se ve esta pantalla: la animación se
 * congelaba ~8 fotogramas y luego saltaba. `transform` lo corre el hilo de
 * composición, aparte, así que no se entera de lo que haga el JavaScript.
 */
const RazaLoader: React.FC<RazaLoaderProps> = ({
    message,
    size = 'md',
    fullScreen = false,
}) => {
    const aro = SIZE_PX[size];
    const caja = Math.round((aro * CAJA_NATURAL) / ARO_NATURAL);
    const escala = aro / ARO_NATURAL;

    const spinner = (
        <div className="flex flex-col items-center justify-center gap-4">
            <div style={{ width: caja, height: caja }}>
                <div
                    className="rz-aro"
                    style={{
                        width: CAJA_NATURAL,
                        height: CAJA_NATURAL,
                        transform: escala === 1 ? undefined : `scale(${escala})`,
                        transformOrigin: 'top left',
                    }}
                >
                    <div className="rz-y"><div className="rz-x" /></div>
                </div>
            </div>
            {message && (
                /* Sin `animate-pulse`: el texto ya está en pantalla desde
                   `index.html`, y verlo arrancar a parpadear justo al montar
                   React delataría el relevo. */
                <p className="text-acero-500 text-sm font-medium">{message}</p>
            )}
        </div>
    );

    if (fullScreen) {
        return (
            <div
                className="fixed inset-0 z-[9999] bg-white flex items-center justify-center"
                role="status"
                aria-label={message || 'Cargando'}
            >
                {spinner}
            </div>
        );
    }

    return (
        <div
            className="h-full w-full min-h-[200px] flex items-center justify-center"
            role="status"
            aria-label={message || 'Cargando'}
        >
            {spinner}
        </div>
    );
};

export default RazaLoader;
